import {
  ActionTypes as FeedItemActionTypes,
  IArchiveLeadSuccessAction,
} from '@app/components/FeedItem/modules/feedItems'
import { IAttachment } from '@app/models/Attachment'
import {
  EntityPermissions,
  IEntityPermissions,
} from '@app/models/EntityPermissions'
import { IListing, Listing } from '@app/models/Listing'
import { ApplicationState } from '@app/store'
import { errorToast } from '@app/utils/notifications'
import { makeAuthenticatedRequest, RequestMethod } from '@app/utils/request'
import { List, Record } from 'immutable'
import { push } from 'connected-react-router'
import { Action, Reducer } from 'redux'
import { fork, put, takeLatest } from 'redux-saga/effects'
import { createSelector } from 'reselect'

// -------------------------
// STATE
// -------------------------

export interface IListingState {
  readonly error?: string
  readonly isDeleting: boolean
  readonly isLoading: boolean
  readonly listing: IListing
  readonly permissions: IEntityPermissions
  readonly showConfirmDelete: boolean
}

const ListingStateRecord = Record({
  error: undefined,
  isDeleting: false,
  isLoading: false,
  listing: new Listing(),
  permissions: new EntityPermissions(),
  showConfirmDelete: false,
})

export class ListingState extends ListingStateRecord implements IListingState {
  public static fromJS(initialState?: Partial<IListingState>) {
    if (!initialState) {
      return new ListingState()
    }

    const { listing, permissions, ...rest } = initialState
    return new ListingState({
      listing: Listing.fromJS(listing),
      permissions: EntityPermissions.fromJS(permissions),
      ...rest,
    })
  }

  public readonly error?: string
  public readonly isDeleting: boolean
  public readonly isLoading: boolean
  public readonly listing: Listing
  public readonly permissions: EntityPermissions
  public readonly showConfirmDelete: boolean

  constructor(props?: Partial<ListingState>) {
    props ? super(props) : super()
  }
}

// -------------------------
// ACTIONS
// -------------------------

export enum ActionTypes {
  DeleteListing = 'LISTING::DELETE',
  DeleteListingFailure = 'LISTING::DELETE_FAILURE',
  DeleteListingRequest = 'LISTING::DELETE_REQUEST',
  DeleteListingSuccess = 'LISTING::DELETE_SUCCESS',
  LoadListing = 'LISTING::LOAD',
  LoadListingFailure = 'LISTING::LOAD_FAILURE',
  LoadListingRequest = 'LISTING::LOAD_REQUEST',
  LoadListingSuccess = 'LISTING::LOAD_SUCCESS',
  ResetState = 'LISTING::RESET_STATE',
  SetAttachments = 'LISTING::SET_ATTACHMENTS',
  ToggleConfirmDelete = 'LISTING::TOGGLE_CONFIRM_DELETE',
}

interface IDeleteListingAction {
  listingId: string
  type: ActionTypes.DeleteListing
}

interface IDeleteListingFailureAction {
  error: string
  type: ActionTypes.DeleteListingFailure
}

interface IDeleteListingRequestAction {
  type: ActionTypes.DeleteListingRequest
}

interface IDeleteListingSuccessAction {
  type: ActionTypes.DeleteListingSuccess
}

interface ILoadListingAction {
  listingId: string
  type: ActionTypes.LoadListing
}

interface ILoadListingFailureAction {
  error: string
  type: ActionTypes.LoadListingFailure
}

interface ILoadListingRequestAction {
  type: ActionTypes.LoadListingRequest
}

interface ILoadListingSuccessAction {
  listing: Listing
  permissions: EntityPermissions
  type: ActionTypes.LoadListingSuccess
}

interface IResetStateAction {
  type: ActionTypes.ResetState
}

interface ISetAttachmentsAction {
  type: ActionTypes.SetAttachments
  value: List<IAttachment>
}

interface IToggleConfirmDeleteAction {
  show: boolean
  type: ActionTypes.ToggleConfirmDelete
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | IArchiveLeadSuccessAction
  | IDeleteListingFailureAction
  | IDeleteListingRequestAction
  | IDeleteListingSuccessAction
  | ILoadListingFailureAction
  | ILoadListingRequestAction
  | ILoadListingSuccessAction
  | IResetStateAction
  | ISetAttachmentsAction
  | IToggleConfirmDeleteAction

// -------------------------
// ACTION CREATORS
// -------------------------
function deleteListing(listingId: string): IDeleteListingAction {
  return {
    listingId,
    type: ActionTypes.DeleteListing,
  }
}

function deleteListingFailure(error: string): IDeleteListingFailureAction {
  return {
    error,
    type: ActionTypes.DeleteListingFailure,
  }
}

function deleteListingRequest(): IDeleteListingRequestAction {
  return {
    type: ActionTypes.DeleteListingRequest,
  }
}

function deleteListingSuccess(): IDeleteListingSuccessAction {
  return {
    type: ActionTypes.DeleteListingSuccess,
  }
}

function loadListing(listingId: string): ILoadListingAction {
  return {
    listingId,
    type: ActionTypes.LoadListing,
  }
}

function loadListingFailure(error: string): ILoadListingFailureAction {
  return {
    error,
    type: ActionTypes.LoadListingFailure,
  }
}

function loadListingRequest(): ILoadListingRequestAction {
  return {
    type: ActionTypes.LoadListingRequest,
  }
}

function loadListingSuccess(
  listing: Listing,
  permissions: EntityPermissions,
): ILoadListingSuccessAction {
  return {
    listing,
    permissions,
    type: ActionTypes.LoadListingSuccess,
  }
}

function resetState(): IResetStateAction {
  return { type: ActionTypes.ResetState }
}

function setAttachments(value: List<IAttachment>): ISetAttachmentsAction {
  return {
    type: ActionTypes.SetAttachments,
    value,
  }
}

function toggleConfirmDelete(show: boolean): IToggleConfirmDeleteAction {
  return {
    show,
    type: ActionTypes.ToggleConfirmDelete,
  }
}

export const actionCreators = {
  deleteListing,
  loadListing,
  resetState,
  setAttachments,
  toggleConfirmDelete,
}

// -------------------------
// REDUCER
// -------------------------

export const reducer: Reducer<ListingState> = (
  state: ListingState = new ListingState(),
  incomingAction: Action,
) => {
  const action = incomingAction as KnownAction
  switch (action.type) {
    case FeedItemActionTypes.ArchiveLeadSuccess:
      const leadIndex = state.listing.property.leads.findIndex((item: any) => {
        return item.id === action.id
      })

      if (leadIndex > -1) {
        return state.setIn(
          ['listing', 'property', 'leads', leadIndex, 'isArchived'],
          true,
        ) as ListingState
      }

      return state

    case ActionTypes.DeleteListingFailure:
      return state
        .set('error', action.error)
        .set('isDeleting', false) as ListingState

    case ActionTypes.DeleteListingRequest:
      return state.set('isDeleting', true) as ListingState

    case ActionTypes.DeleteListingSuccess:
      return state
        .set('isDeleting', false)
        .set('listing', new Listing()) as ListingState

    case ActionTypes.LoadListingFailure:
      return state
        .set('error', action.error)
        .set('isLoading', false) as ListingState

    case ActionTypes.LoadListingRequest:
      return state.set('isLoading', true) as ListingState

    case ActionTypes.LoadListingSuccess:
      return state
        .set('error', undefined)
        .set('isLoading', false)
        .set('listing', action.listing)
        .set('permissions', action.permissions) as ListingState

    case ActionTypes.ResetState:
      return state
        .set('error', undefined)
        .set('listing', new Listing()) as ListingState

    case ActionTypes.SetAttachments:
      return state.setIn(
        ['listing', 'property', 'attachments'],
        action.value,
      ) as ListingState

    case ActionTypes.ToggleConfirmDelete:
      return state.set('showConfirmDelete', action.show) as ListingState

    default:
      // The following line guarantees that every action in the KnownAction union has been covered by a case above
      const exhaustiveCheck: never = action
  }

  return state
}

// -------------------------
// SAGAS
// -------------------------
function* deleteListingSaga(action: IDeleteListingAction) {
  try {
    yield put(deleteListingRequest())

    const result = yield makeAuthenticatedRequest(
      `/api/Listings/${action.listingId}`,
      { method: RequestMethod.DELETE },
    )

    if (result.success === true) {
      yield put(push('/brokerage/listings'))
      yield put(deleteListingSuccess())
    } else {
      yield put(deleteListingFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(deleteListingFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* loadListingSaga(action: ILoadListingAction) {
  try {
    yield put(loadListingRequest())

    const url = `/api/Listings/${action.listingId}`

    const result = yield makeAuthenticatedRequest(url)

    if (result.success === true) {
      yield put(
        loadListingSuccess(
          Listing.fromJS(result.entity),
          EntityPermissions.fromJS(result.permissions),
        ),
      )
    } else {
      yield put(loadListingFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(loadListingFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* watchDeleteListing() {
  yield takeLatest(ActionTypes.DeleteListing, deleteListingSaga)
}

function* watchLoadListing() {
  yield takeLatest(ActionTypes.LoadListing, loadListingSaga)
}

export function* listingRootSaga() {
  yield fork(watchDeleteListing)
  yield fork(watchLoadListing)
}

// -------------------------
// SELECTORS
// -------------------------
const selectModuleState = () => (state: ApplicationState) => state.listing

export const selectError = () =>
  createSelector(selectModuleState(), (mState) => mState.error)

export const selectIsDeleting = () =>
  createSelector(selectModuleState(), (mState) => mState.isDeleting)

export const selectIsLoading = () =>
  createSelector(selectModuleState(), (mState) => mState.isLoading)

export const selectListing = () =>
  createSelector(selectModuleState(), (mState) => mState.listing)

export const selectPermissions = () =>
  createSelector(selectModuleState(), (mState) => mState.permissions)

export const selectShowConfirmDelete = () =>
  createSelector(selectModuleState(), (mState) => mState.showConfirmDelete)
